import React, { useEffect, useRef, useState, useCallback } from "react";
import styled, { keyframes, createGlobalStyle } from "styled-components";
import { db, auth, googleProvider } from "../firebase";
import { ref, onValue, push, update, remove, get } from "firebase/database";
import { signInWithPopup, signOut, onAuthStateChanged } from "firebase/auth";

const LEGACY_ITEMS = "sigalGear/items"; // pre-auth global list, migrated to first signer
const userItemsRef = (uid) => `sigalGear/users/${uid}/items`;
const SEARCH_ENDPOINT = "https://sigal-gear-search.miller-kfir.workers.dev";

/** parse a pasted/imported list into {name, qty}[] — supports CSV and free text */
function parseList(text) {
  return String(text || "")
    .split(/\r?\n/)
    .map((raw) => {
      const line = raw.trim();
      if (!line) return null;
      // skip our own export header / total rows
      if (/^פריט\s*,/.test(line) || /^"?סה/.test(line)) return null;
      let name = line, qty = 1;
      if (line.includes(",")) {
        const cols = line.split(",");
        name = cols[0].trim().replace(/^"|"$/g, "");
        const q = parseInt(cols[1], 10);
        if (!Number.isNaN(q) && q > 0) qty = q;
      } else {
        const m = line.match(/^(.*\S)\s*[x×*]\s*(\d+)$/i) || line.match(/^(.*\S)\s+(\d+)$/);
        if (m) { name = m[1].trim(); qty = parseInt(m[2], 10) || 1; }
      }
      name = name.replace(/^[-•*\d.)\s]+/, "").trim(); // strip bullet/number prefixes
      return name ? { name, qty } : null;
    })
    .filter(Boolean);
}

const ils = (n) =>
  "₪" +
  (Number(n) || 0).toLocaleString("he-IL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

/* ============================================================
   Price search against the SerpApi cloud function
   ============================================================ */
async function fetchLowestPrices(query) {
  const r = await fetch(`${SEARCH_ENDPOINT}?q=${encodeURIComponent(query)}`, {
    headers: { Accept: "application/json" },
  });
  const ct = r.headers.get("content-type") || "";
  if (!r.ok || !ct.includes("application/json")) {
    throw new Error("search-failed");
  }
  const data = await r.json();
  return Array.isArray(data.results) ? data.results : [];
}

/* ---------- export the list to a CSV that Excel/Sheets opens (with Hebrew) ---------- */
function exportExcel(items) {
  const rows = items
    .filter((it) => (it.name || "").trim())
    .map((it) => {
      const qty = Number(it.qty) || 0;
      const price = Number(it.price) || 0;
      return [it.name, qty, price, (qty * price).toFixed(2), it.source || "", it.link || ""];
    });
  const total = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.price) || 0), 0);
  const header = ["פריט", "כמות", "מחיר ליחידה", 'סה"כ', "חנות", "קישור"];
  const data = [header, ...rows, [], ['סה"כ', "", "", total.toFixed(2), "", ""]];
  const esc = (v) => {
    const s = String(v ?? "");
    return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const csv = data.map((r) => r.map(esc).join(",")).join("\r\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "רשימת-ציוד-אומנות.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* ============================================================
   Page
   ============================================================ */
export const SigalGear = () => {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [consolidating, setConsolidating] = useState(false);
  const [consolidateMsg, setConsolidateMsg] = useState("");
  const [authUser, setAuthUser] = useState(undefined); // undefined=loading, null=signed out
  const [authBusy, setAuthBusy] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const fileRef = useRef(null);

  const uid = authUser ? authUser.uid : null;
  const REF = uid ? userItemsRef(uid) : null;

  // page title + meta description, scoped to this route
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "רשימת ציוד אומנות - סיגל מילר";
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute("content") : null;
    if (meta) {
      meta.setAttribute(
        "content",
        "רשימת ציוד אומנות לכיתה - חיפוש המחיר הזול ביותר בישראל וחישוב סכום כולל"
      );
    }
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc != null) meta.setAttribute("content", prevDesc);
    };
  }, []);

  useEffect(() => onAuthStateChanged(auth, (u) => setAuthUser(u || null)), []);

  useEffect(() => {
    if (!uid) { setItems([]); setLoaded(false); return; }
    const unsub = onValue(ref(db, userItemsRef(uid)), (snap) => {
      const val = snap.val() || {};
      const arr = Object.entries(val)
        .map(([id, v]) => ({ id, ...v }))
        .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
      setItems(arr);
      setLoaded(true);
    });
    return () => unsub();
  }, [uid]);

  // one-time migration: legacy global list → first signed-in user
  useEffect(() => {
    if (!uid) return;
    (async () => {
      const mine = await get(ref(db, userItemsRef(uid)));
      if (mine.exists()) return;
      const leg = await get(ref(db, LEGACY_ITEMS));
      if (!leg.exists()) return;
      await update(ref(db, `sigalGear/users/${uid}`), { items: leg.val() || {} });
      await remove(ref(db, LEGACY_ITEMS));
    })().catch(() => {});
  }, [uid]);

  const addItem = () => {
    push(ref(db, REF), { name: "", qty: 1, price: 0, link: "", source: "", title: "", createdAt: Date.now() });
  };

  const clearAll = () => {
    if (!items.length) return;
    if (!window.confirm("לנקות את כל הרשימה? הפעולה אינה הפיכה.")) return;
    remove(ref(db, REF));
  };

  const importList = (list) => {
    if (!list.length || !REF) return;
    const updates = {};
    const t = Date.now();
    list.forEach((it, i) => {
      const key = push(ref(db, REF)).key;
      updates[key] = { name: it.name, qty: it.qty || 1, price: 0, link: "", source: "", title: "", createdAt: t + i };
    });
    update(ref(db, REF), updates);
  };

  const onFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => importList(parseList(String(reader.result || "")));
    reader.readAsText(f, "utf-8");
    e.target.value = "";
  };

  const doPaste = () => { importList(parseList(pasteText)); setPasteText(""); setPasteOpen(false); };

  const signIn = async () => {
    setAuthBusy(true);
    try { await signInWithPopup(auth, googleProvider); }
    catch (e) { alert("ההתחברות נכשלה. ודאו ש-Google מופעל ב-Firebase (Authentication)."); }
    finally { setAuthBusy(false); }
  };
  const signOutNow = () => signOut(auth).catch(() => {});

  const grandTotal = items.reduce(
    (s, it) => s + (Number(it.qty) || 0) * (Number(it.price) || 0),
    0
  );
  const totalUnits = items.reduce((s, it) => s + (Number(it.qty) || 0), 0);
  const pricedCount = items.filter((it) => Number(it.price) > 0).length;
  const namedItems = items.filter((it) => (it.name || "").trim().length >= 2);

  // Search every item, then pick the single shop that carries the most items
  // and switch those items to that shop's (cheapest) offer — fewer orders.
  const consolidate = useCallback(async () => {
    if (!namedItems.length) return;
    setConsolidating(true);
    setConsolidateMsg("מחפש בכל החנויות…");
    try {
      const perItem = await Promise.all(
        namedItems.map(async (it) => {
          try {
            return { id: it.id, results: await fetchLowestPrices(it.name.trim()) };
          } catch {
            return { id: it.id, results: [] };
          }
        })
      );
      // shop -> { byItem: {itemId: {price, link}} }
      const shops = {};
      perItem.forEach(({ id, results }) => {
        results.forEach((r) => {
          const shop = (r.source || "").trim();
          if (!shop || !r.link || !(r.price > 0)) return;
          shops[shop] = shops[shop] || { byItem: {} };
          const cur = shops[shop].byItem[id];
          if (!cur || r.price < cur.price) shops[shop].byItem[id] = { price: r.price, link: r.link };
        });
      });
      const ranked = Object.entries(shops)
        .map(([name, s]) => {
          const ids = Object.keys(s.byItem);
          const sum = ids.reduce((t, id) => t + s.byItem[id].price, 0);
          return { name, count: ids.length, sum, byItem: s.byItem };
        })
        .sort((a, b) => b.count - a.count || a.sum - b.sum);

      if (!ranked.length) {
        setConsolidateMsg("לא נמצאו חנויות מתאימות.");
        return;
      }
      const best = ranked[0];
      const updates = {};
      Object.entries(best.byItem).forEach(([id, offer]) => {
        updates[`${id}/price`] = offer.price;
        updates[`${id}/link`] = offer.link;
        updates[`${id}/source`] = best.name;
      });
      await update(ref(db, REF), updates);
      setConsolidateMsg(
        `🏪 ${best.name} מכסה ${best.count} מתוך ${namedItems.length} פריטים — עודכנו לחנות זו.`
      );
    } catch {
      setConsolidateMsg("אירעה שגיאה בריכוז החנויות.");
    } finally {
      setConsolidating(false);
    }
  }, [namedItems, REF]);

  if (authUser === undefined) {
    return <Page dir="rtl"><GlobalKiller /><Centered>טוען…</Centered></Page>;
  }
  if (!authUser) {
    return (
      <Page dir="rtl"><GlobalKiller />
        <GateCenter>
          <GateWrap>
            <GateBrush>🎨</GateBrush>
            <GateTitle>רשימת ציוד אומנות</GateTitle>
            <GateText>התחברו עם חשבון Google כדי לראות ולנהל את רשימת הציוד שלכם. לכל מורה הרשימה הפרטית שלו.</GateText>
            <GoogleBtn onClick={signIn} disabled={authBusy}>
              <GBtnG>G</GBtnG>{authBusy ? "מתחבר…" : "התחברות עם Google"}
            </GoogleBtn>
          </GateWrap>
        </GateCenter>
        <Credit>כל הזכויות שמורות © לכפיר מילר</Credit>
      </Page>
    );
  }

  return (
    <Page dir="rtl">
      <GlobalKiller />
      <Inner>
        <Header>
          <TitleGroup>
            <Brushes aria-hidden="true">🎨</Brushes>
            <Title>רשימת ציוד אומנות</Title>
          </TitleGroup>
        </Header>

        <UserBar>
          {authUser.photoURL ? <Avatar src={authUser.photoURL} alt="" /> : <AvatarEmoji>👤</AvatarEmoji>}
          <UserName>{authUser.displayName || (authUser.email && authUser.email.split("@")[0])}</UserName>
          <SignOut onClick={signOutNow}>יציאה</SignOut>
        </UserBar>

        <Toolbar>
          <AddBtn onClick={addItem}>＋ הוסף פריט</AddBtn>
          <Counts>
            {items.length} פריטים · {totalUnits} יחידות
          </Counts>
          <MenuWrap>
            <Hamburger onClick={() => setMenuOpen((o) => !o)} aria-label="תפריט פעולות">☰</Hamburger>
            {menuOpen && (
              <>
                <MenuBackdrop onClick={() => setMenuOpen(false)} />
                <Menu>
                  <MenuItem disabled={!namedItems.length} onClick={() => { setMenuOpen(false); exportExcel(items); }}>📊 יצוא לאקסל</MenuItem>
                  <MenuItem disabled={consolidating || !namedItems.length} onClick={() => { setMenuOpen(false); consolidate(); }}>
                    {consolidating ? "מרכז…" : "🏪 רכז לחנות אחת"}
                  </MenuItem>
                  <MenuItem onClick={() => { setMenuOpen(false); setPasteOpen(true); }}>📋 הדבק רשימה</MenuItem>
                  <MenuItem onClick={() => { setMenuOpen(false); fileRef.current && fileRef.current.click(); }}>📥 ייבא רשימה</MenuItem>
                  <MenuItem $danger disabled={!items.length} onClick={() => { setMenuOpen(false); clearAll(); }}>🗑 נקה הכל</MenuItem>
                </Menu>
              </>
            )}
            <input ref={fileRef} type="file" accept=".csv,.txt" style={{ display: "none" }} onChange={onFile} />
          </MenuWrap>
        </Toolbar>

        {consolidateMsg && (
          <Banner onClick={() => setConsolidateMsg("")}>{consolidateMsg}</Banner>
        )}

        {!loaded ? (
          <Empty>טוען…</Empty>
        ) : items.length === 0 ? (
          <Empty>
            אין עדיין פריטים ברשימה.
            <br />
            לחצו על <b>״＋ הוסף פריט״</b>, או הדביקו/ייבאו רשימה.
          </Empty>
        ) : (
          <List>
            {items.map((it) => (
              <ItemCard key={it.id} item={it} uid={uid} />
            ))}
          </List>
        )}

        <div style={{ height: 120 }} />
      </Inner>

      {pasteOpen && (
        <PasteOverlay onClick={(e) => { if (e.target === e.currentTarget) setPasteOpen(false); }}>
          <PasteModal>
            <PasteTitle>הדבקת רשימה</PasteTitle>
            <PasteHint>פריט אחד בכל שורה. אפשר להוסיף כמות: "צבעי גואש, 3" או "מכחולים x2".</PasteHint>
            <PasteArea
              autoFocus
              value={pasteText}
              placeholder={"צבעי גואש 12 גוונים, 3\nמכחולים שטוחים x2\nדפי בריסטול"}
              onChange={(e) => setPasteText(e.target.value)}
            />
            <PasteActions>
              <AddBtn onClick={doPaste} disabled={!parseList(pasteText).length}>הוסף לרשימה</AddBtn>
              <SecBtn onClick={() => setPasteOpen(false)}>ביטול</SecBtn>
            </PasteActions>
          </PasteModal>
        </PasteOverlay>
      )}

      <TotalBar>
        <TotalInner>
          <TotalLabel>
            סה״כ ({pricedCount}/{items.length} פריטים עם מחיר)
          </TotalLabel>
          <TotalValue>{ils(grandTotal)}</TotalValue>
        </TotalInner>
        <BarCredit>כל הזכויות שמורות © לכפיר מילר</BarCredit>
      </TotalBar>
    </Page>
  );
};

/* ============================================================
   Item card
   ============================================================ */
function ItemCard({ item, uid }) {
  const REF = userItemsRef(uid);
  const [name, setName] = useState(item.name || "");
  const [link, setLink] = useState(item.link || "");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null); // array of alternatives, or null
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false); // expanded alternatives / custom url

  const nameTimer = useRef(null);
  const linkTimer = useRef(null);
  const lastSearched = useRef(item.name || "");

  const patch = useCallback(
    (p) => update(ref(db, `${REF}/${item.id}`), p),
    [item.id, REF]
  );

  const runSearch = useCallback(
    async (q) => {
      const query = (q ?? name).trim();
      if (query.length < 2) return;
      setLoading(true);
      setError("");
      lastSearched.current = query;
      try {
        const res = await fetchLowestPrices(query);
        setResults(res);
        if (res.length > 0) {
          const best = res[0];
          setLink(best.link);
          patch({
            price: best.price,
            link: best.link,
            source: best.source || "",
            title: best.title || "",
            searchedAt: Date.now(),
          });
        } else {
          setError("לא נמצאו תוצאות");
        }
      } catch (e) {
        setError("שגיאה בחיפוש, נסו שוב");
      } finally {
        setLoading(false);
      }
    },
    [name, patch]
  );

  // debounced name → save + auto-search
  const onNameChange = (v) => {
    setName(v);
    if (nameTimer.current) clearTimeout(nameTimer.current);
    nameTimer.current = setTimeout(() => {
      patch({ name: v });
      if (v.trim().length >= 2 && v.trim() !== lastSearched.current) {
        runSearch(v);
      }
    }, 800);
  };

  // debounced custom link save
  const onLinkChange = (v) => {
    setLink(v);
    if (linkTimer.current) clearTimeout(linkTimer.current);
    linkTimer.current = setTimeout(() => patch({ link: v, source: "ידני" }), 600);
  };

  const setQty = (q) => {
    const next = Math.max(1, Math.min(999, q));
    patch({ qty: next });
  };

  const setPrice = (v) => {
    const n = v === "" ? 0 : Math.max(0, Number(v) || 0);
    patch({ price: n });
  };

  const chooseAlt = (alt) => {
    setLink(alt.link);
    patch({
      price: alt.price,
      link: alt.link,
      source: alt.source || "",
      title: alt.title || "",
      searchedAt: Date.now(),
    });
    setOpen(false);
  };

  useEffect(
    () => () => {
      if (nameTimer.current) clearTimeout(nameTimer.current);
      if (linkTimer.current) clearTimeout(linkTimer.current);
    },
    []
  );

  const qty = Number(item.qty) || 0;
  const price = Number(item.price) || 0;
  const lineTotal = qty * price;

  return (
    <Card>
      <TopRow>
        <NameWrap>
          <NameInput
            value={name}
            placeholder="שם הפריט (למשל: צבעי גואש 12 גוונים)"
            onChange={(e) => onNameChange(e.target.value)}
          />
          {loading && <Spinner title="מחפש…" />}
        </NameWrap>
        <RemoveBtn onClick={() => remove(ref(db, `${REF}/${item.id}`))} title="מחק פריט">
          ✕
        </RemoveBtn>
      </TopRow>

      <MidRow>
        <QtyBox>
          <StepBtn onClick={() => setQty(qty - 1)} aria-label="פחות">
            −
          </StepBtn>
          <QtyNum
            type="number"
            min="1"
            value={qty}
            onChange={(e) => setQty(parseInt(e.target.value || "1", 10))}
          />
          <StepBtn onClick={() => setQty(qty + 1)} aria-label="עוד">
            ＋
          </StepBtn>
        </QtyBox>

        <PriceBox>
          <PriceLabel>מחיר ליחידה</PriceLabel>
          <PriceField>
            <span>₪</span>
            <PriceInput
              type="number"
              min="0"
              step="0.5"
              value={price || ""}
              placeholder="0"
              onChange={(e) => setPrice(e.target.value)}
            />
          </PriceField>
        </PriceBox>

        <LineTotal>
          <PriceLabel>סה״כ פריט</PriceLabel>
          <LineTotalVal>{ils(lineTotal)}</LineTotalVal>
        </LineTotal>
      </MidRow>

      <BottomRow>
        <SearchBtn onClick={() => runSearch()} disabled={loading || name.trim().length < 2}>
          {loading ? "מחפש…" : "🔎 חפש מחיר זול"}
        </SearchBtn>

        {item.link ? (
          <BuyLink href={item.link} target="_blank" rel="noopener noreferrer">
            🛒 לקנייה{item.source ? ` · ${item.source}` : ""} ↗
          </BuyLink>
        ) : (
          <NoLink>אין קישור עדיין</NoLink>
        )}

        <ToggleBtn onClick={() => setOpen((o) => !o)}>
          {open ? "סגור" : "קישור משלי / חלופות"}
        </ToggleBtn>
      </BottomRow>

      {error && <ErrorMsg>{error}</ErrorMsg>}

      {open && (
        <Details>
          <CustomUrl>
            <CustomLabel>קישור משלך (URL):</CustomLabel>
            <UrlInput
              value={link}
              placeholder="הדבק כאן קישור לחנות…"
              onChange={(e) => onLinkChange(e.target.value)}
            />
            <SmallBtn onClick={() => runSearch()} disabled={loading}>
              חפש מחדש
            </SmallBtn>
          </CustomUrl>

          {results && results.length > 0 && (
            <Alts>
              <AltsTitle>חלופות מחיר (מהזול ליקר):</AltsTitle>
              {results.map((alt, i) => (
                <AltRow key={i} $best={i === 0}>
                  <AltPrice>{ils(alt.price)}</AltPrice>
                  <AltSource title={alt.title}>{alt.source || alt.title || "חנות"}</AltSource>
                  <AltActions>
                    <AltLink href={alt.link} target="_blank" rel="noopener noreferrer">
                      צפייה ↗
                    </AltLink>
                    <ChooseBtn onClick={() => chooseAlt(alt)}>בחר</ChooseBtn>
                  </AltActions>
                </AltRow>
              ))}
            </Alts>
          )}
        </Details>
      )}
    </Card>
  );
}

/* ============================================================
   Styles
   ============================================================ */
const spin = keyframes`to { transform: rotate(360deg); }`;
const fadeIn = keyframes`from { opacity: 0; transform: translateY(6px);} to {opacity:1; transform:none;}`;

const GlobalKiller = createGlobalStyle`
  body { background: #fbf7f0; }
  #root * { box-sizing: border-box; }
`;

/* ---------- auth gate + user bar + credit (shared design with art-lesson-plan) ---------- */
const Centered = styled.div`
  min-height: 60vh; display: flex; align-items: center; justify-content: center;
  font-family: "Heebo", sans-serif; font-size: 18px; font-weight: 700; color: #9a8f80;
`;
const GateCenter = styled.div`
  min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 16px;
`;
const GateWrap = styled.div`
  width: 100%; max-width: 460px; padding: 32px 24px; text-align: center;
  background: #fff; border: 1px solid #efe4d4; border-radius: 20px; box-shadow: 0 10px 40px rgba(80,60,30,.1);
`;
const GateBrush = styled.div`font-size: 56px; line-height: 1; margin-bottom: 6px;`;
const GateTitle = styled.h1`
  margin: 0; font-size: 30px; font-weight: 900; text-align: center;
  background: linear-gradient(135deg, #e0533b, #e08a2b, #4f9d8c, #4f7fc4, #8b5cf6);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
`;
const GateText = styled.p`font-size: 15px; font-weight: 600; color: #8a7c6a; line-height: 1.7; margin: 16px 0 22px;`;
const GoogleBtn = styled.button`
  display: inline-flex; align-items: center; gap: 10px; border: 2px solid #e6d9c6; cursor: pointer;
  font-family: inherit; font-size: 16px; font-weight: 800; color: #3c3744; background: #fff;
  padding: 11px 26px; border-radius: 12px; box-shadow: 0 4px 14px rgba(80,60,30,.1);
  &:hover:not(:disabled){ background: #faf4ec; } &:disabled{ opacity:.6; cursor:not-allowed; }
`;
const GBtnG = styled.span`
  font-family: Arial, sans-serif; font-weight: 900; font-size: 18px;
  background: conic-gradient(from -45deg, #ea4335, #fbbc05, #34a853, #4285f4, #ea4335);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
`;
const UserBar = styled.div`display: flex; align-items: center; gap: 10px; margin-bottom: 14px;`;
const Avatar = styled.img`width: 30px; height: 30px; border-radius: 50%; border: 2px solid #efe4d4;`;
const AvatarEmoji = styled.div`
  width: 30px; height: 30px; border-radius: 50%; background: #f3eefc; display: flex;
  align-items: center; justify-content: center; font-size: 16px;
`;
const UserName = styled.div`font-size: 14px; font-weight: 800; color: #5a5060; flex: 1;`;
const SignOut = styled.button`
  border: 1.5px solid #e6d9c6; background: #fff; color: #8a7c6a; font-family: inherit; font-weight: 700;
  font-size: 13px; padding: 7px 14px; border-radius: 10px; cursor: pointer; &:hover{ background:#faf4ec; }
`;
const Credit = styled.div`
  position: fixed; left: 0; right: 0; bottom: 0; z-index: 5;
  text-align: center; font-size: 11px; font-weight: 600; color: #a89c8a;
  padding: 7px 8px; pointer-events: none;
  background: linear-gradient(to top, rgba(244,236,224,0.95), rgba(244,236,224,0));
`;
const BarCredit = styled.div`
  text-align: center; font-size: 10.5px; font-weight: 600; color: #9a90b0; padding: 0 0 6px;
`;

/* ---------- secondary toolbar buttons + paste modal ---------- */
const SecBtn = styled.button`
  border: 2px solid #d8cbb6; cursor: pointer; font-family: inherit; font-size: 14px; font-weight: 800;
  color: #6b5a44; background: #faf4ec; padding: 9px 16px; border-radius: 12px;
  &:hover:not(:disabled){ background: #f2e8d8; } &:disabled{ opacity:.5; cursor:not-allowed; }
`;
const ClearBtn = styled.button`
  border: 2px solid #e7b7ab; cursor: pointer; font-family: inherit; font-size: 14px; font-weight: 800;
  color: #c0654a; background: #fbeee8; padding: 9px 16px; border-radius: 12px;
  &:hover:not(:disabled){ background: #f6ddd2; } &:disabled{ opacity:.45; cursor:not-allowed; }
`;
const PasteOverlay = styled.div`
  position: fixed; inset: 0; z-index: 60; background: rgba(20,12,30,.55); backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center; padding: 16px; direction: rtl;
`;
const PasteModal = styled.div`
  background: #fff; border-radius: 16px; padding: 18px; width: min(480px, 100%);
  box-shadow: 0 20px 60px rgba(0,0,0,.4);
`;
const PasteTitle = styled.div`font-weight: 900; font-size: 19px; color: #2c2640; margin-bottom: 4px;`;
const PasteHint = styled.div`font-size: 13px; font-weight: 600; color: #9a8f80; line-height: 1.6; margin-bottom: 10px;`;
const PasteArea = styled.textarea`
  width: 100%; min-height: 160px; resize: vertical; font-family: inherit; font-size: 15px; color: #2c2640;
  border: 1.5px solid #e6d9c6; border-radius: 12px; padding: 11px; outline: none;
  &:focus{ border-color: #e08a2b; }
`;
const PasteActions = styled.div`display: flex; gap: 10px; margin-top: 12px;`;

/* ---------- hamburger actions menu ---------- */
const MenuWrap = styled.div`position: relative;`;
const Hamburger = styled.button`
  border: 2px solid #d8cbb6; background: #faf4ec; color: #6b5a44; cursor: pointer;
  width: 48px; height: 46px; border-radius: 12px; font-size: 22px; line-height: 1; font-weight: 900;
  &:hover{ background: #f2e8d8; }
`;
const MenuBackdrop = styled.div`position: fixed; inset: 0; z-index: 40;`;
const Menu = styled.div`
  position: absolute; top: calc(100% + 6px); left: 0; z-index: 41;
  background: #fff; border: 1px solid #e6d9c6; border-radius: 12px; padding: 6px;
  box-shadow: 0 10px 30px rgba(80, 60, 30, 0.18); min-width: 200px;
  display: flex; flex-direction: column; gap: 2px;
`;
const MenuItem = styled.button`
  text-align: right; border: none; background: transparent; cursor: pointer; font-family: inherit;
  font-size: 14px; font-weight: 700; color: ${(p) => (p.$danger ? "#c0654a" : "#3c3744")};
  padding: 10px 12px; border-radius: 8px; white-space: nowrap;
  &:hover:not(:disabled) { background: ${(p) => (p.$danger ? "#fbeee8" : "#faf4ec")}; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const Page = styled.div`
  min-height: 100%;
  background:
    radial-gradient(900px 500px at 12% -5%, rgba(255, 138, 101, 0.18), transparent 60%),
    radial-gradient(900px 500px at 95% 0%, rgba(126, 178, 235, 0.18), transparent 60%),
    linear-gradient(180deg, #fbf7f0 0%, #f4ece0 100%);
  font-family: "Heebo", "Assistant", system-ui, sans-serif;
  color: #2c2640;
`;

const Inner = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 22px 16px 0;
`;

const Header = styled.div`
  direction: rtl; /* title group pinned right, name pushed left */
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin-bottom: 18px;
`;

const TitleGroup = styled.div`
  direction: ltr; /* 🎨 on the left of the title */
  display: inline-flex;
  align-items: baseline;
  gap: 10px;
`;

const Brushes = styled.div`
  font-size: clamp(28px, 6vw, 38px);
  line-height: 1;
  align-self: center;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(22px, 5.5vw, 34px);
  font-weight: 900;
  letter-spacing: -0.5px;
  white-space: nowrap;
  background: linear-gradient(100deg, #e0533b, #e08a2b, #4f9d8c, #4f7fc4, #8b5cf6);
  background-size: 220% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  filter: drop-shadow(0 2px 8px rgba(224, 83, 59, 0.18));
`;

const Subtitle = styled.div`
  font-size: clamp(14px, 3.6vw, 18px);
  font-weight: 800;
  color: #7a5bbf;
  white-space: nowrap;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const AddBtn = styled.button`
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 16px;
  font-weight: 800;
  color: #fff;
  padding: 11px 22px;
  border-radius: 12px;
  background: linear-gradient(135deg, #e0533b, #e08a2b);
  box-shadow: 0 6px 16px rgba(224, 83, 59, 0.3);
  transition: transform 0.1s, box-shadow 0.2s;

  &:active {
    transform: translateY(2px);
    box-shadow: 0 2px 8px rgba(224, 83, 59, 0.3);
  }
`;

const ExportBtn = styled.button`
  border: 2px solid #2f8a6a;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  font-weight: 800;
  color: #2f8a6a;
  background: #eef6f1;
  padding: 9px 16px;
  border-radius: 12px;
  transition: background 0.15s, opacity 0.15s;
  &:hover:not(:disabled) { background: #dff0e7; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const ConsolidateBtn = styled.button`
  border: 2px solid #4f7fc4;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  font-weight: 800;
  color: #3a6bb0;
  background: #eef3fb;
  padding: 9px 16px;
  border-radius: 12px;
  transition: background 0.15s, opacity 0.15s;
  &:hover:not(:disabled) { background: #e0ebf8; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const Banner = styled.div`
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  color: #3a4a66;
  background: #eef3fb;
  border: 1.5px solid #c7d8f0;
  border-radius: 12px;
  padding: 11px 14px;
  margin-bottom: 14px;
  line-height: 1.6;
`;

const Counts = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #9a8f80;
`;

const Empty = styled.div`
  text-align: center;
  padding: 48px 20px;
  color: #9a8f80;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.9;
  background: #fff;
  border: 2px dashed #e6d9c6;
  border-radius: 16px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #efe4d4;
  border-radius: 16px;
  padding: 14px;
  box-shadow: 0 4px 16px rgba(80, 60, 30, 0.06);
  animation: ${fadeIn} 0.25s ease;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const NameWrap = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
`;

const NameInput = styled.input`
  width: 100%;
  font-family: inherit;
  font-size: 17px;
  font-weight: 700;
  color: #2c2640;
  border: none;
  border-bottom: 2px solid #efe4d4;
  background: transparent;
  padding: 6px 2px;
  outline: none;
  transition: border-color 0.2s;
  &:focus {
    border-color: #e08a2b;
  }
  &::placeholder {
    color: #c4b9a8;
    font-weight: 600;
  }
`;

const Spinner = styled.span`
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
  margin-inline-start: 8px;
  border: 3px solid #f0e3d0;
  border-top-color: #e08a2b;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

const RemoveBtn = styled.button`
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background: #f7ede3;
  color: #c0654a;
  font-size: 15px;
  font-weight: 900;
  transition: background 0.2s;
  &:hover {
    background: #f4ddd0;
  }
`;

const MidRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 14px;
  margin-top: 14px;
  flex-wrap: wrap;
`;

const QtyBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StepBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1.5px solid #e6d9c6;
  background: #faf4ec;
  color: #6b5a44;
  font-size: 19px;
  font-weight: 800;
  cursor: pointer;
  line-height: 1;
  &:active {
    transform: translateY(1px);
  }
`;

const QtyNum = styled.input`
  width: 46px;
  height: 34px;
  text-align: center;
  font-family: inherit;
  font-size: 16px;
  font-weight: 800;
  border: 1.5px solid #e6d9c6;
  border-radius: 10px;
  color: #2c2640;
  outline: none;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

const PriceBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const PriceLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #b0a392;
`;

const PriceField = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  border: 1.5px solid #e6d9c6;
  border-radius: 10px;
  padding: 0 8px;
  height: 34px;
  background: #fff;
  color: #6b5a44;
  font-weight: 800;
`;

const PriceInput = styled.input`
  width: 72px;
  border: none;
  outline: none;
  font-family: inherit;
  font-size: 16px;
  font-weight: 800;
  color: #2c2640;
  background: transparent;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

const LineTotal = styled.div`
  margin-inline-start: auto;
  display: flex;
  flex-direction: column;
  gap: 3px;
  align-items: flex-end;
`;

const LineTotalVal = styled.div`
  font-size: 20px;
  font-weight: 900;
  color: #2f8a6a;
`;

const BottomRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  flex-wrap: wrap;
`;

const SearchBtn = styled.button`
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  font-weight: 800;
  color: #fff;
  padding: 9px 16px;
  border-radius: 10px;
  background: linear-gradient(135deg, #4f7fc4, #4f9d8c);
  box-shadow: 0 4px 12px rgba(79, 127, 196, 0.25);
  transition: transform 0.1s, opacity 0.2s;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:active:not(:disabled) {
    transform: translateY(2px);
  }
`;

const BuyLink = styled.a`
  font-size: 14px;
  font-weight: 800;
  color: #2f8a6a;
  text-decoration: none;
  padding: 9px 14px;
  border-radius: 10px;
  background: #e9f6ef;
  border: 1.5px solid #bfe6d2;
  transition: background 0.2s;
  &:hover {
    background: #d8f0e3;
  }
`;

const NoLink = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #b0a392;
`;

const ToggleBtn = styled.button`
  margin-inline-start: auto;
  border: none;
  background: transparent;
  color: #7a5bbf;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
`;

const ErrorMsg = styled.div`
  margin-top: 10px;
  font-size: 13px;
  font-weight: 700;
  color: #c0654a;
  background: #fbeee8;
  border-radius: 8px;
  padding: 7px 10px;
`;

const Details = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #efe4d4;
  animation: ${fadeIn} 0.2s ease;
`;

const CustomUrl = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const CustomLabel = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #6b5a44;
`;

const UrlInput = styled.input`
  flex: 1;
  min-width: 180px;
  direction: ltr;
  text-align: left;
  font-family: inherit;
  font-size: 13px;
  border: 1.5px solid #e6d9c6;
  border-radius: 8px;
  padding: 7px 10px;
  outline: none;
  color: #2c2640;
  &:focus {
    border-color: #7a5bbf;
  }
`;

const SmallBtn = styled.button`
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 800;
  color: #fff;
  padding: 8px 14px;
  border-radius: 8px;
  background: #7a5bbf;
  &:disabled {
    opacity: 0.5;
  }
`;

const Alts = styled.div`
  margin-top: 12px;
`;

const AltsTitle = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: #b0a392;
  margin-bottom: 6px;
`;

const AltRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 10px;
  background: ${(p) => (p.$best ? "#eef6f1" : "#faf6ef")};
  border: 1px solid ${(p) => (p.$best ? "#c3e7d4" : "#efe4d4")};
  margin-bottom: 6px;
`;

const AltPrice = styled.div`
  font-size: 16px;
  font-weight: 900;
  color: #2f8a6a;
  min-width: 64px;
`;

const AltSource = styled.div`
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  color: #6b5a44;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AltActions = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const AltLink = styled.a`
  font-size: 12px;
  font-weight: 700;
  color: #4f7fc4;
  text-decoration: none;
  white-space: nowrap;
`;

const ChooseBtn = styled.button`
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: 800;
  color: #fff;
  padding: 6px 12px;
  border-radius: 8px;
  background: #2f8a6a;
`;

const TotalBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(44, 38, 64, 0.97);
  backdrop-filter: blur(6px);
  box-shadow: 0 -6px 20px rgba(0, 0, 0, 0.18);
  z-index: 20;
`;

const TotalInner = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TotalLabel = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #cdc4e0;
`;

const TotalValue = styled.div`
  font-size: 26px;
  font-weight: 900;
  color: #ffd27a;
`;
